import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import SideBar from "../../components/SideBar";
import { Components } from "../../assets/Dimensions";
import { toast } from "react-toastify";
import "../questionVideoSolution/style.css";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import { useHistory } from "react-router-dom";
import Loader from "@material-ui/core/CircularProgress";
import {
  GetAllSegments,
  GetSegmentTopics,
  GetCourseNames,
  GetWorklistByCourseName,
  GetSegmentCoursesByWorklist,
} from "../../api/CMS";
import ContentType from "../../enums/ContentType";

// ─── Constants ───────────────────────────────────────────────────────────────

const COURSE_NAME_STORAGE_KEY = "cmsEditor_selectedCourseName";
const COURSE_ID_STORAGE_KEY   = "cmsEditor_selectedCourseId";
const GOAL_STORAGE_KEY        = "cmsEditor_selectedGoal";

const TaskStatusLabelsTr = {
  0: "Yok",
  1: "Yapılacak",
  2: "Devam Ediyor",
  3: "İnceleme Ret",
  4: "İncelemede",
  5: "Tamamlandı",
  6: "Hata",
};

const TaskStatusColors = {
  1: "#9ca3af", // Todo          – gray     (not started, neutral)
  2: "#FFFF00", // InProgress    – yellow     (actively working)
  3: "#f97316", // PendingReview – orange   (waiting / needs attention)
  4: "#3b82f6", // InReview      – blue   (review pipeline)
  5: "#22c55e", // Done          – green    (complete — universal success signal)
  6: "#ef4444", // OnError       – red      (problem)
};

// ─── Card Helpers ─────────────────────────────────────────────────────────────

// Left border color based on card's error / completion status
const getCardBorderLeft = (course) => {
  if (!Array.isArray(course.Counts) || !course.TotalCount) return "none";
  const onError = course.Counts.find((c) => c.TaskStatus === 6);
  if (onError && onError.Count > 0) return "4px solid #ef4444";
  const done = course.Counts.find((c) => c.TaskStatus === 5);
  if (done && done.Count === course.TotalCount) return "4px solid #22c55e";
  return "none";
};

// Thin progress bar: Done / TotalCount
const CardProgressBar = ({ counts, totalCount }) => {
  if (!totalCount) return null;
  const done = Array.isArray(counts)
    ? (counts.find((c) => c.TaskStatus === 5)?.Count ?? 0)
    : 0;
  const pct = Math.round((done / totalCount) * 100);
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          height: 6,
          backgroundColor: "#e5e7eb",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            backgroundColor: pct === 100 ? "#22c55e" : "#3b82f6",
            borderRadius: 3,
            transition: "width 0.4s ease",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          fontSize: 11,
          color: "#6b7280",
          marginTop: 3,
        }}
      >
        {done} / {totalCount} tamamlandı
      </div>
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const CoursePage = () => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 700px)" });
  const user = JSON.parse(localStorage.getItem("user"));
  const history = useHistory();

  // Modal visibility
  const [isSelectingCourse, setIsSelectingCourse] = useState(false);
  const [isSelectingJob, setIsSelectingJob] = useState(false);

  // Data
  const [courseNames, setCourseNames] = useState([]);       // Ders list
  const [selectedCourseName, setSelectedCourseName] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [worklistData, setWorklistData] = useState([]);     // Jobs for selected Ders
  const [selectedGoal, setSelectedGoal] = useState("");
  const [segmentCourses, setSegmentCourses] = useState([]); // Cards
  const [allCourseData, setAllCourseData] = useState(null); // GetAllSegments – for SubCourses allSegmentData

  // Accordion
  const [openCourseId, setOpenCourseId] = useState(null);
  const [topicsByCourse, setTopicsByCourse] = useState({});

  const [loader, setLoader] = useState(false);

  // ─── Style ─────────────────────────────────────────────────────────────────

  const style = {
    container: {
      paddingLeft: isTabletOrMobile ? 15 : Components.SideBarWidth + 15,
      paddingTop: 20,
      paddingRight: 15,
      marginBottom: 20,
    },
    iconButton: { marginLeft: 10, padding: 0 },
    editIcon: {
      width: Components.Image.Big.Width,
      height: Components.Image.Big.Height,
    },
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const groupByTitle = (items) =>
    items.reduce((acc, curr) => {
      if (!acc[curr.Title]) acc[curr.Title] = [];
      acc[curr.Title].push(curr);
      return acc;
    }, {});

  const getRoleCount = (item) => {
    // if (user.Role === "TeacherEditors") 
    return item.EditorRemainingCount;
    // if (user.Role === "ContentApprovers") return item.ApproverRemainingCount;
    // return item.RemainingCount; // Administrators
  };

  // ─── API Calls ─────────────────────────────────────────────────────────────

  const getAllData = async () => {
    try {
      setLoader(true);

      // Always fetch GetAllSegments for SubCourses allSegmentData dependency
      const segmentsRes = await GetAllSegments(user.Token);
      if (segmentsRes.data.ResponseStatus === 1) {
        setAllCourseData(segmentsRes.data.ResponseData);
        localStorage.setItem(
          "MaarifModelData",
          JSON.stringify(segmentsRes.data.ResponseData)
        );
      }

      // Fetch Ders (course names) list
      const courseRes = await GetCourseNames(user.Token);
      if (courseRes.data.ResponseStatus === 1) {
        setCourseNames(courseRes.data.ResponseData);
      } else {
        toast.error("Ders listesi alınamadı.");
        return;
      }

      // Restore saved Ders + İş if available
      const savedCourseName = localStorage.getItem(COURSE_NAME_STORAGE_KEY);
      const savedCourseIdRaw = localStorage.getItem(COURSE_ID_STORAGE_KEY);
      const savedGoal = localStorage.getItem(GOAL_STORAGE_KEY);

      if (savedCourseName && savedCourseIdRaw) {
        const courseId = parseInt(savedCourseIdRaw, 10);
        setSelectedCourseName(savedCourseName);
        setSelectedCourseId(courseId);
        if (savedGoal) {
          // Both course and job saved → load cards directly, skip all modals
          setSelectedGoal(savedGoal);
          await loadSegmentCourses(courseId, savedGoal);
          await loadWorklist(courseId);
        } else {
          await loadWorklist(courseId);
          setIsSelectingJob(true);
        }
      } else {
        setIsSelectingCourse(true);
      }
    } catch (error) {
      console.error("getAllData error:", error);
      toast.error("Bir hata oluştu, lütfen yetkililerle iletişime geçiniz.");
    } finally {
      setLoader(false);
    }
  };

  const loadWorklist = async (courseId) => {
    try {
      setLoader(true);
      const res = await GetWorklistByCourseName(courseId, user.Token);
      if (res.data.ResponseStatus === 1) {
        setWorklistData(res.data.ResponseData || []);
      } else {
        localStorage.removeItem(COURSE_NAME_STORAGE_KEY);
        localStorage.removeItem(COURSE_ID_STORAGE_KEY);
        localStorage.removeItem(GOAL_STORAGE_KEY);
        toast.error("İş listesi alınamadı.");
      }
    } catch (error) {
      localStorage.removeItem(COURSE_NAME_STORAGE_KEY);
      localStorage.removeItem(COURSE_ID_STORAGE_KEY);
      localStorage.removeItem(GOAL_STORAGE_KEY);
      console.error("loadWorklist error:", error);
      toast.error("Bir hata oluştu, lütfen yetkililerle iletişime geçiniz.");
    } finally {
      setLoader(false);
    }
  };

  const loadSegmentCourses = async (courseId, goal) => {
    try {
      setLoader(true);
      const res = await GetSegmentCoursesByWorklist(courseId, goal, user.Token);
      if (res.data.ResponseStatus === 1) {
        setSegmentCourses(res.data.ResponseData || []);
      } else {
        localStorage.removeItem(COURSE_NAME_STORAGE_KEY);
        localStorage.removeItem(COURSE_ID_STORAGE_KEY);
        localStorage.removeItem(GOAL_STORAGE_KEY);
        toast.error("Veri alınamadı. Lütfen tekrar deneyiniz.");
        history.push("./userprofile");
      }
    } catch (error) {
      localStorage.removeItem(COURSE_NAME_STORAGE_KEY);
      localStorage.removeItem(COURSE_ID_STORAGE_KEY);
      localStorage.removeItem(GOAL_STORAGE_KEY);
      console.error("loadSegmentCourses error:", error);
      toast.error("Bir hata oluştu, lütfen yetkililerle iletişime geçiniz.");
    } finally {
      setLoader(false);
    }
  };

  const loadTopicsForCourse = async (courseId) => {
    try {
      setLoader(true);
      const res = await GetSegmentTopics(courseId, selectedGoal, user.Token);
      if (res.data.ResponseStatus === 1) {
        const grouped = groupByTitle(res.data.ResponseData);
        setTopicsByCourse((prev) => ({ ...prev, [courseId]: grouped }));
      } else {
        toast.error("Konular alınamadı.");
      }
    } catch (error) {
      console.error("loadTopicsForCourse error:", error);
      toast.error("Bir hata oluştu, lütfen yetkililerle iletişime geçiniz.");
    } finally {
      setLoader(false);
    }
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleCourseSelect = async (course) => {
    setSelectedCourseName(course.Name);
    setSelectedCourseId(course.CourseId);
    localStorage.setItem(COURSE_NAME_STORAGE_KEY, course.Name);
    localStorage.setItem(COURSE_ID_STORAGE_KEY, String(course.CourseId));
    localStorage.removeItem(GOAL_STORAGE_KEY); // clear old job when course changes
    // Reset downstream state when Ders changes
    setSelectedGoal("");
    setSegmentCourses([]);
    setOpenCourseId(null);
    setTopicsByCourse({});
    setIsSelectingCourse(false);
    await loadWorklist(course.CourseId);
    setIsSelectingJob(true);
  };

  const handleJobSelect = async (jobName) => {
    localStorage.setItem(GOAL_STORAGE_KEY, jobName);
    setSelectedGoal(jobName);
    setIsSelectingJob(false);
    setOpenCourseId(null);
    setTopicsByCourse({});
    await loadSegmentCourses(selectedCourseId, jobName);
  };

  const handleChangeCourse = () => {
    setIsSelectingCourse(true);
  };

  // Open the job modal — keep selectedGoal + segmentCourses so close/backdrop
  // returns the user to their existing cards without losing state
  const handleChangeJob = () => {
    setOpenCourseId(null);
    setTopicsByCourse({});
    setIsSelectingJob(true);
  };

  const handleBackToCourseSelection = () => {
    localStorage.removeItem(COURSE_NAME_STORAGE_KEY);
    localStorage.removeItem(COURSE_ID_STORAGE_KEY);
    localStorage.removeItem(GOAL_STORAGE_KEY);
    setSelectedCourseName(null);
    setSelectedCourseId(null);
    setWorklistData([]);
    setIsSelectingJob(false);
    setIsSelectingCourse(true);
  };

  const toggleAccordion = (courseId) => {
    if (openCourseId === courseId) {
      setOpenCourseId(null);
    } else {
      setOpenCourseId(courseId);
      if (!topicsByCourse[courseId]) {
        loadTopicsForCourse(courseId);
      }
    }
  };

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    getAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Navigation helper ─────────────────────────────────────────────────────

  const buildNavigationState = (course, extra = {}) => ({
    courseId: course.Id,
    courseName: course.Name,
    classTypeId: null,
    contentType: ContentType.Courses,
    segmentId: null,
    allSegmentData: allCourseData,
    goal: selectedGoal,
    ...extra,
  });

  // ─── Render ────────────────────────────────────────────────────────────────

  // Close button + backdrop are only shown when there are cards to return to
  const isClosable = segmentCourses.length > 0;

  return (
    <>
      {/* Sidebar */}
      {isTabletOrMobile ? (
        <div>
          <span
            style={{ position: "absolute", top: 10, right: 10 }}
            onClick={() => {}}
          >
            menu
          </span>
        </div>
      ) : (
        <SideBar />
      )}

      {/* Full-screen loader */}
      {loader && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <Loader size={100} />
        </div>
      )}

      <div style={style.container}>

        {/* ── Modal 1: Ders Seçimi ───────────────────────────────────────── */}
        {isSelectingCourse && (
          <div
            className="modal cms-panel-modal"
            onClick={isClosable ? () => setIsSelectingCourse(false) : undefined}
          >
            <div
              className="modal-content"
              style={{ paddingTop: isClosable ? 44 : undefined }}
              onClick={(e) => e.stopPropagation()}
            >
              {isClosable && (
                <button
                  className="modal-close-btn"
                  aria-label="Kapat"
                  onClick={() => setIsSelectingCourse(false)}
                >
                  ✕
                </button>
              )}
              <div style={{ textAlign: "center" }}>
                <h2>Lütfen Ders Seçiniz</h2>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    flexWrap: "wrap",
                    gap: 10,
                    marginTop: 16,
                  }}
                >
                  {courseNames.map((course) => (
                    <button
                      key={course.CourseId}
                      style={{
                        padding: "10px 14px",
                        width: isTabletOrMobile ? "45%" : "30%",
                        marginBottom: 10,
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 14,
                        backgroundColor:
                          selectedCourseId === course.CourseId
                            ? "#2563eb"
                            : "#f9fafb",
                        color:
                          selectedCourseId === course.CourseId
                            ? "#fff"
                            : "#111827",
                      }}
                      onClick={() => handleCourseSelect(course)}
                    >
                      {course.Name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal 2: İş Seçimi ─────────────────────────────────────────── */}
        {isSelectingJob && (
          <div
            className="modal cms-panel-modal"
            onClick={isClosable ? () => setIsSelectingJob(false) : undefined}
          >
            <div
              className="modal-content"
              style={{ paddingTop: isClosable ? 44 : undefined }}
              onClick={(e) => e.stopPropagation()}
            >
              {isClosable && (
                <button
                  className="modal-close-btn"
                  aria-label="Kapat"
                  onClick={() => setIsSelectingJob(false)}
                >
                  ✕
                </button>
              )}
              <div style={{ textAlign: "center" }}>
                <h2>Lütfen İş Seçiniz</h2>

                {/* Selected Ders + change link */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    marginBottom: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: 14 }}>
                    Ders:{" "}
                    <strong style={{ color: "#111827" }}>{selectedCourseName}</strong>
                  </span>
                  <button
                    onClick={handleBackToCourseSelection}
                    style={{
                      background: "none",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      padding: "3px 10px",
                      fontSize: 12,
                      color: "#2563eb",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    ← Ders Değiştir
                  </button>
                </div>

                {/* Job list or empty state */}
                {worklistData.length === 0 ? (
                  <div
                    style={{
                      padding: "24px 16px",
                      backgroundColor: "#f9fafb",
                      borderRadius: 8,
                      border: "1px dashed #d1d5db",
                      color: "#6b7280",
                      fontSize: 14,
                    }}
                  >
                    Bu ders için henüz iş bulunamamıştır.
                    <br />
                    <span style={{ fontSize: 12 }}>
                      Farklı bir ders seçmek için yukarıdaki "Ders Değiştir" butonunu kullanın.
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      maxHeight: "50vh",
                      overflowY: "auto",
                      padding: "0 4px",
                    }}
                  >
                    {worklistData.map((item, index) => {
                      const count = getRoleCount(item);
                      const total = item.TotalCount;
                      const countLabel =
                        total !== undefined && total !== null
                          ? `${count} / ${total}`
                          : count;
                      const isAcil = item.Name.toLowerCase().includes("acil");
                      return (
                        <button
                          key={index}
                          className={isAcil ? "acil-job-btn" : undefined}
                          onClick={() => handleJobSelect(item.Name)}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: isAcil ? "1.5px solid #ef4444" : "1px solid #d1d5db",
                            backgroundColor: isAcil ? "#fff5f5" : "#f9fafb",
                            cursor: "pointer",
                            textAlign: "left",
                            width: "100%",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = isAcil ? "#fee2e2" : "#eff6ff")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = isAcil ? "#fff5f5" : "#f9fafb")
                          }
                        >
                          <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
                            {item.Name}
                          </span>
                          {(count > 0 || total > 0) && (
                            <span
                              style={{
                                backgroundColor: isAcil ? "#fee2e2" : "#dbeafe",
                                color: isAcil ? "#dc2626" : "#1d4ed8",
                                borderRadius: 12,
                                padding: "2px 10px",
                                fontSize: 13,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {countLabel}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Page header ────────────────────────────────────────────────── */}
        <div style={{ fontWeight: "bold", fontSize: "20px" }}>
          Konu ve Kazanım Belirleme Paneli
        </div>

        {/* ── Selected Ders + legend row ─────────────────────────────────── */}
        {segmentCourses.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              margin: 10,
            }}
          >
            {/* Change-Ders button */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                alignItems: "flex-end",
              }}
            >
              <div
                style={{
                  borderWidth: 2,
                  borderColor: "black",
                  borderRadius: 8,
                  padding: 10,
                  borderStyle: "solid",
                  cursor: "pointer",
                  alignItems: "center",
                  display: "flex",
                }}
                onClick={handleChangeCourse}
              >
                <span style={{ fontWeight: 600 }}>{selectedCourseName}</span>
                <IconButton style={style.iconButton}>
                  <EditIcon style={style.editIcon} />
                </IconButton>
              </div>
              {selectedGoal && (
                <div
                  style={{
                    borderWidth: 2,
                    borderColor: "black",
                    borderRadius: 8,
                    padding: 10,
                    borderStyle: "solid",
                    cursor: "pointer",
                    alignItems: "center",
                    display: "flex",
                    marginTop: 4,
                  }}
                  onClick={handleChangeJob}
                >
                  <span style={{ fontWeight: 600 }}>İş: {selectedGoal}</span>
                  <IconButton style={style.iconButton}>
                    <EditIcon style={style.editIcon} />
                  </IconButton>
                </div>
              )}
            </div>

            {/* Status legend */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                padding: "10px 0",
                alignItems: "center",
              }}
            >
              {Object.entries(TaskStatusLabelsTr)
                .filter(([key]) => key !== "0")
                .map(([key, label]) => (
                  <div
                    key={key}
                    style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 14 }}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        backgroundColor: TaskStatusColors[key],
                      }}
                    />
                    <span>{label}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* ── Course cards ───────────────────────────────────────────────── */}
        {segmentCourses.map((course) => (
          <div key={course.Id}>
            <div
              style={{
                padding: 24,
                borderRadius: 8,
                cursor: "pointer",
                marginBottom: 10,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                transition: "transform 0.15s ease-in-out",
                // borderLeft: getCardBorderLeft(course),
              }}
            >
              {/* Top row: name + stats */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 18, fontWeight: "bold" }}>
                  {course.Name}
                </div>

                {course.TotalCount > 0 && (
                  <div
                    style={{
                      backgroundColor: "#42486d",
                      borderRadius: 6,
                      padding: "6px 12px",
                      display: "flex",
                      gap: 8,
                      color: "#111",
                      fontWeight: 600,
                      position: "relative",
                      cursor: "default",
                    }}
                    className="stats-container"
                  >
                    {Array.isArray(course.Counts) &&
                      course.Counts.map((item) => (
                        <span
                          key={item.TaskStatus}
                          style={{ color: TaskStatusColors[item.TaskStatus] }}
                        >
                          {item.Count}
                        </span>
                      ))}

                    {/* Tooltip */}
                    <div className="stats-tooltip" style={tooltipStyle}>
                      <ul style={{ listStyleType: "disc", margin: 0, paddingLeft: 20 }}>
                        {Array.isArray(course.Counts) &&
                          course.Counts.map((item) => (
                            <li
                              key={item.TaskStatus}
                              style={{
                                color: TaskStatusColors[item.TaskStatus],
                                fontWeight: 500,
                                marginBottom: 4,
                              }}
                            >
                              {TaskStatusLabelsTr[item.TaskStatus]} ({item.Count})
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {/* <CardProgressBar counts={course.Counts} totalCount={course.TotalCount} /> */}

              {/* Bottom row: buttons */}
              <div
                className="card-buttons"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  gap: 12,
                  marginTop: 10,
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => toggleAccordion(course.Id)}
                  style={btnStyle("#2563eb")}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                  Konu Seç
                </button>

                <button
                  onClick={() =>
                    history.push({
                      pathname: "/NewTopicAndAchSelectionQuestionPage",
                      state: buildNavigationState(course),
                    })
                  }
                  style={btnStyle("#16a34a")}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                  Başla
                </button>
              </div>
            </div>

            {/* Accordion */}
            {openCourseId === course.Id && (
              <div
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: 8,
                  marginTop: 5,
                  padding: "12px 16px",
                  marginBottom: 10,
                }}
              >
                {!topicsByCourse[course.Id] ? (
                  <div style={{ textAlign: "center", padding: 10 }}>
                    Konular yükleniyor...
                  </div>
                ) : (
                  Object.entries(topicsByCourse[course.Id]).map(
                    ([topicTitle, subtopics]) => (
                      <div key={topicTitle} style={{ marginBottom: 12 }}>
                        <div
                          style={{
                            fontWeight: "bold",
                            fontSize: 16,
                            marginBottom: 6,
                            color: "#1f2937",
                          }}
                        >
                          {topicTitle}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "no-wrap",
                            flexDirection: "column",
                            gap: "8px",
                            marginLeft: 10,
                          }}
                        >
                          {subtopics.map((sub) => (
                            <div
                              key={sub.Id}
                              style={{
                                backgroundColor: "#e5e7eb",
                                borderRadius: 6,
                                padding: "6px 10px",
                                cursor: "pointer",
                                transition: "background 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                justifyContent: "space-between",
                              }}
                              onClick={() =>
                                history.push({
                                  pathname: "/NewTopicAndAchSelectionQuestionPage",
                                  state: {
                                    ...buildNavigationState(course),
                                    topicName: topicTitle,
                                    SegmentTopicId: sub.Id,
                                    subTopicName: sub.Name,
                                    contentType: ContentType.SubCourseTopic,
                                  },
                                })
                              }
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor = "#d1d5db")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor = "#e5e7eb")
                              }
                            >
                              <div style={{ fontWeight: 600, color: "#111827" }}>
                                {sub.Name}
                              </div>

                              {/* Sub-topic stats pill */}
                              {sub.TotalCount > 0 && (
                                <div
                                  className="stats-container"
                                  style={{
                                    backgroundColor: "#fff",
                                    borderRadius: 6,
                                    padding: "6px 12px",
                                    display: "flex",
                                    gap: 8,
                                    color: "#111",
                                    fontWeight: 600,
                                    position: "relative",
                                    cursor: "default",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {Array.isArray(sub.Counts) &&
                                    sub.Counts.map((item) => (
                                      <span
                                        key={item.TaskStatus}
                                        style={{
                                          color: TaskStatusColors[item.TaskStatus],
                                        }}
                                      >
                                        {item.Count}
                                      </span>
                                    ))}

                                  {/* Sub-topic tooltip */}
                                  <div className="stats-tooltip" style={tooltipStyle}>
                                    <ul style={{ listStyleType: "disc", margin: 0, paddingLeft: 20 }}>
                                      {Array.isArray(sub.Counts) &&
                                        sub.Counts.map((item) => (
                                          <li
                                            key={item.TaskStatus}
                                            style={{
                                              color: TaskStatusColors[item.TaskStatus],
                                              fontWeight: 500,
                                              marginBottom: 4,
                                            }}
                                          >
                                            {TaskStatusLabelsTr[item.TaskStatus]} (
                                            {item.Count})
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Shared CSS */}
      <style>{`
        .stats-container:hover .stats-tooltip {
          visibility: visible !important;
          opacity: 1 !important;
        }
        @media (min-width: 768px) {
          .card-buttons button { flex: none !important; width: 150px !important; }
        }
        @media (max-width: 767px) {
          .card-buttons button { flex: 1 1 45% !important; width: 100% !important; }
        }

        /* ── Acil (urgent) job pulse animation ── */
        @keyframes acil-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45); }
          60%       { box-shadow: 0 0 0 7px rgba(239, 68, 68, 0); }
        }
        .acil-job-btn {
          animation: acil-pulse 2s ease-out infinite;
        }

        /* ── Scoped CMS panel modal overrides (does not affect other pages) ── */
        .cms-panel-modal .modal-content {
          position: relative;
          max-width: 580px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-sizing: border-box;
        }
        .modal-close-btn {
          position: absolute;
          top: 10px;
          right: 12px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 22px;
          line-height: 1;
          color: #9ca3af;
          padding: 2px 6px;
          border-radius: 4px;
          transition: color 0.15s, background-color 0.15s;
          min-width: 32px;
          min-height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .modal-close-btn:hover {
          color: #111827;
          background-color: #f3f4f6;
        }
        @media (max-width: 768px) {
          .cms-panel-modal .modal-content {
            width: 92%;
            padding: 16px;
          }
        }
        @media (max-width: 480px) {
          .cms-panel-modal .modal-content {
            width: 95%;
            padding: 12px;
            border-radius: 6px;
          }
        }
      `}</style>
    </>
  );
};

// ─── Shared style helpers ─────────────────────────────────────────────────────

const tooltipStyle = {
  visibility: "hidden",
  opacity: 0,
  position: "absolute",
  top: "120%",
  right: 0,
  backgroundColor: "#42486d",
  color: "#111",
  padding: "10px 14px",
  borderRadius: 8,
  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
  fontSize: 14,
  transition: "opacity 0.2s ease, visibility 0.2s ease",
  zIndex: 10,
  minWidth: 180,
};

const btnStyle = (bg) => ({
  flex: "1 1 45%",
  maxWidth: 160,
  minWidth: 120,
  padding: "10px 0",
  backgroundColor: bg,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  transition: "transform 0.1s ease, opacity 0.2s ease",
});

export default CoursePage;
